import React, { useMemo, useState } from 'react';
import { Button, Menu, Progress, Typography } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import './AgreementStep.css';

import ServicesSection from './sections/ServicesSection';
import SubscriptionFeesSection from './sections/SubscriptionFeesSection';
import PaymentAuthorizationSection from './sections/PaymentAuthorizationSection';
import CustomerResponsibilitiesSection from './sections/CustomerResponsibilitiesSection';
import CustomerDataSection from './sections/CustomerDataSection';
import DataExportRetentionSection from './sections/DataExportRetentionSection';
import IntellectualPropertySection from './sections/IntellectualPropertySection';
import SupportSection from './sections/SupportSection';
import TerminationSection from './sections/TerminationSection';
import DisclaimerWarrantiesSection from './sections/DisclaimerWarrantiesSection';
import LimitationLiabilitySection from './sections/LimitationLiabilitySection';
import GoverningLawSection from './sections/GoverningLawSection';
import EntireAgreementSection from './sections/EntireAgreementSection';


const { Title, Paragraph, Text } = Typography;

const AgreementOverviewSection = ({ form }) => {
  return (
    <div>
      <Paragraph>
        This Software Subscription Agreement ("Agreement") is entered into by and between
        Club Sports Solutions L.L.C., a Texas Limited Liability Company ("Provider"), and the
        customer identified below ("Customer").
      </Paragraph>

      <div className="agreements-review-box">
        <p><strong>Effective Date:</strong> July 1, 2026</p>

        <p>
          <strong>Provider:</strong> Club Sports Solutions L.L.C.<br />
          <strong>Provider Type:</strong> Texas Limited Liability Company
        </p>

        <p>
          <strong>Customer Name:</strong> {form.ownerName}<br />
          <strong>Customer Business Name:</strong> {form.legalBusinessName}<br />
          <strong>Customer Email:</strong> {form.email}
        </p>

        <p><strong>Monthly Subscription Fee:</strong> $34.00/month</p>
      </div>

      <Paragraph>
        This Agreement governs Customer's access to and use of the TrackBiz software platform,
        including inventory management, member/customer management, POS, order management,
        appointment management, reporting, and related services made available by Provider.
      </Paragraph>
    </div>
  );
};

const buildAgreementSections = (form) => [
  {
    key: 'agreement-overview',
    number: null,
    title: 'Agreement Overview',
    component: <AgreementOverviewSection form={form} />,
  },
  {
    key: 'services',
    number: 1,
    title: 'Services',
    component: <ServicesSection />,
    content: [
      'Provider grants Customer a non-exclusive, non-transferable right to access and use the TrackBiz software platform for Customer\'s internal business operations.',
      'TrackBiz may include features such as Inventory Management, Point of Sale (POS), Order Management, Customer Management, Appointment Scheduling, Reporting and Analytics, and other features made available by Provider.',
      'As part of the subscription, Provider may create and host a basic business website for Customer at no additional charge. Customer is responsible for purchasing, renewing, and maintaining any domain name registrations required for the website.',
    ],
  },
  {
    key: 'subscription-fees',
    number: 2,
    title: 'Subscription Fees',
    component: <SubscriptionFeesSection />,
    content: [
      'Customer agrees to pay a monthly subscription fee of $34.00 per month.',
      'Subscription fees will be billed automatically through the payment method maintained on file. All subscription fees are non-refundable except as required by law.',
      'Hardware charges, including card readers or other equipment purchased on Customer\'s behalf, are separate from subscription fees.',
    ],
  },
  {
    key: 'payment-authorization',
    number: 3,
    title: 'Payment Authorization',
    component: <PaymentAuthorizationSection />,
    content: [
      'Customer authorizes recurring monthly billing through Provider\'s payment processor for all subscription fees due under this Agreement.',
      'Customer agrees to maintain a valid payment method at all times.',
      'If any payment remains unpaid for more than fifteen (15) days after the due date, Provider may suspend Customer\'s access to TrackBiz until all outstanding amounts have been paid.',
    ],
  },
  {
    key: 'customer-responsibilities',
    number: 4,
    title: 'Customer Responsibilities',
    component: <CustomerResponsibilitiesSection />,
    content: [
      'Customer is responsible for maintaining accurate inventory information, pricing information, customer records, appointment records, employee access credentials, and account usernames and passwords.',
      'Customer is responsible for complying with all applicable laws and regulations.',
      'Provider is not responsible for losses resulting from incorrect, incomplete, or outdated information entered by Customer or Customer\'s employees.',
    ],
  },
  {
    key: 'customer-data',
    number: 5,
    title: 'Customer Data',
    component: <CustomerDataSection />,
    content: [
      'Customer retains ownership of all business data entered into TrackBiz.',
      'Provider may access Customer data only as reasonably necessary to provide technical support, troubleshoot issues, maintain and improve the software, or comply with legal obligations.',
      'Provider will use commercially reasonable efforts to protect Customer data from unauthorized access, but cannot guarantee absolute security of Customer data.',
    ],
  },
  {
    key: 'data-export-retention',
    number: 6,
    title: 'Data Export and Retention',
    component: <DataExportRetentionSection />,
    content: [
      'Upon termination of this Agreement, Customer may request an export of its data within thirty (30) days.',
      'Provider may permanently delete Customer data at any time after the thirty (30) day period has expired.',
      'Customer is responsible for maintaining copies of any exported reports or business records.',
    ],
  },
  {
    key: 'intellectual-property',
    number: 7,
    title: 'Intellectual Property',
    component: <IntellectualPropertySection />,
    content: [
      'TrackBiz and all related software, source code, designs, documentation, trademarks, and materials remain the exclusive property of Club Sports Solutions L.L.C.',
      'This Agreement grants Customer a limited, non-exclusive, non-transferable right to use TrackBiz during the term of this Agreement and does not transfer ownership of any intellectual property.',
      'Customer retains ownership of its business name, logos, trademarks, photographs, content, customer information, and other materials provided by Customer for use within TrackBiz or Customer\'s website.',
    ],
  },
  {
    key: 'support',
    number: 8,
    title: 'Support',
    component: <SupportSection />,
    content: [
      'Provider will provide reasonable technical support via email, phone, or remote assistance during normal business hours.',
      'Support includes assistance with the use of TrackBiz and resolution of software-related issues.',
      'Support does not include custom software development, third-party software issues, data entry services, or business consulting services unless separately agreed in writing.',
    ],
  },
  {
    key: 'termination',
    number: 9,
    title: 'Termination',
    component: <TerminationSection />,
    content: [
      'Either party may terminate this Agreement with thirty (30) days written notice.',
      'Provider may immediately suspend or terminate Customer\'s access to TrackBiz if Customer fails to pay fees owed, violates this Agreement, or uses TrackBiz for unlawful purposes.',
      'Upon termination, Customer\'s access to TrackBiz will end. Customer\'s rights regarding data export and retention shall be governed by Section 6 of this Agreement.',
    ],
  },
  {
    key: 'disclaimer-warranties',
    number: 10,
    title: 'Disclaimer of Warranties',
    component: <DisclaimerWarrantiesSection />,
    content: [
      'TrackBiz is provided on an "as available" and "as is" basis.',
      'Provider does not guarantee uninterrupted, error-free, or completely secure operation of the software.',
      'Customer acknowledges that TrackBiz relies on third-party services, including cloud hosting providers, payment processors, domain registrars, internet service providers, and other technology vendors.',
      'Provider shall not be responsible for service interruptions, downtime, delays, or data loss caused by failures or outages of third-party services beyond Provider\'s reasonable control.',
    ],
  },
  {
    key: 'limitation-liability',
    number: 11,
    title: 'Limitation of Liability',
    component: <LimitationLiabilitySection />,
    content: [
      'To the maximum extent permitted by law, Provider\'s total liability arising out of or relating to this Agreement shall not exceed the total subscription fees paid by Customer during the twelve (12) months preceding the claim.',
      'Provider shall not be liable for lost profits, lost revenue, loss of business opportunities, loss of data, or indirect, incidental, special, consequential, or punitive damages.',
      'These limitations shall apply regardless of the legal theory under which a claim is brought.',
    ],
  },
  {
    key: 'governing-law',
    number: 12,
    title: 'Governing Law',
    component: <GoverningLawSection />,
    content: [
      'This Agreement shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law principles.',
      'Any legal action arising out of or relating to this Agreement shall be brought exclusively in the state or federal courts located in Williamson County, Texas, and each party consents to the jurisdiction of such courts.',
    ],
  },
  {
    key: 'entire-agreement',
    number: 13,
    title: 'Entire Agreement',
    component: <EntireAgreementSection />,
    content: [
      'This Agreement constitutes the entire agreement between the parties and supersedes all prior discussions, negotiations, representations, and understandings relating to TrackBiz.',
      'No amendment or modification of this Agreement shall be effective unless made in writing and signed by both parties.',
      'After reviewing all sections, Customer must complete the final signature process through LegalZoom.',
    ],
  },
];

const AgreementStep = ({ form, handleChange, onAgreementConfirmed }) => {
  const agreementSections = useMemo(() => buildAgreementSections(form), [form]);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [reviewedSectionKeys, setReviewedSectionKeys] = useState(new Set([agreementSections[0].key]));

  const currentSection = agreementSections[currentSectionIndex];
  const isLastSection = currentSectionIndex === agreementSections.length - 1;
  const isFirstSection = currentSectionIndex === 0;

  const reviewPercent = useMemo(() => {
    return Math.round((reviewedSectionKeys.size / agreementSections.length) * 100);
  }, [reviewedSectionKeys, agreementSections.length]);

  const markSectionReviewed = (sectionKey) => {
    setReviewedSectionKeys((prev) => {
      const next = new Set(prev);
      next.add(sectionKey);
      return next;
    });
  };

  const goToSection = (sectionIndex) => {
    setCurrentSectionIndex(sectionIndex);
    markSectionReviewed(agreementSections[sectionIndex].key);
  };

  const handlePreviousSection = () => {
    if (isFirstSection) {
      return;
    }

    goToSection(currentSectionIndex - 1);
  };

  const handleNextSection = () => {
    if (isLastSection) {
      return;
    }

    goToSection(currentSectionIndex + 1);
  };

  const menuItems = agreementSections.map((section, index) => ({
    key: section.key,
    label: (
      <span className="agreement-section-menu-item">
        <span>{section.number ? `${section.number}. ${section.title}` : section.title}</span>
        {reviewedSectionKeys.has(section.key) && (
          <CheckCircleFilled className="agreement-section-completed-icon" />
        )}
      </span>
    ),
    onClick: () => goToSection(index),
  }));

  return (
    <div className="agreements-step-content">
      <Title level={2}>Review Agreement</Title>
      <Paragraph>
        Review each section of the TrackBiz Software Subscription Agreement. After reviewing all
        sections, complete the signature process through LegalZoom.
      </Paragraph>

      <div className="agreement-review-progress">
        <Text strong>
          Agreement Review Progress: {reviewedSectionKeys.size} / {agreementSections.length} sections reviewed
        </Text>
        <Progress percent={reviewPercent} size="small" />
      </div>

      <div className="agreement-review-layout">
        <div className="agreement-section-menu">
          <Menu
            mode="inline"
            selectedKeys={[currentSection.key]}
            items={menuItems}
          />
        </div>

        <div className="agreement-section-body">
          {currentSection.number ? (
            <Text type="secondary">Section {currentSection.number} of {agreementSections.length - 1}</Text>
          ) : (
            <Text type="secondary">Agreement Summary</Text>
          )}
          <Title level={3}>
            {currentSection.number ? `${currentSection.number}. ${currentSection.title}` : currentSection.title}
          </Title>

          {currentSection.component}

          {isLastSection && (
            <div className="agreements-review-box">
              <Title level={4}>LegalZoom Signature Required</Title>
              <Paragraph>
                Please complete the final signature process using the TrackBiz agreement sent through LegalZoom.
              </Paragraph>

              <label className="checkbox-row">
                <input
                  name="agreementConfirmed"
                  type="checkbox"
                  checked={form.agreementConfirmed}
                  onChange={handleChange}
                />
                I confirm that I reviewed the agreement sections and completed the LegalZoom signing step.
              </label>
            </div>
          )}

          <div className="agreement-section-actions">
            <Button disabled={isFirstSection} onClick={handlePreviousSection}>
              Previous Section
            </Button>

            {isLastSection ? (
              <Button
                type="primary"
                disabled={!form.agreementConfirmed}
                onClick={() => {
                  markSectionReviewed(currentSection.key);
                  onAgreementConfirmed && onAgreementConfirmed();
                }}
              >
                Confirm Agreement
              </Button>
            ) : (
              <Button type="primary" onClick={handleNextSection}>
                Next Section
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementStep;